<template>
  <div class="container-fluid">
    <div class="row wc-filters-header py-3">
      <div class="col-10">
        <font-awesome-icon :icon="['fa', 'sliders']" />
        <strong class="ml-2"> {{ $t('filters') }}</strong>
      </div>
      <button
        class="unstyled-btn col-2 text-right text-primary"
        @click="close()"
        :aria-label="$t('close')"
      >
        <font-awesome-icon :icon="['fa', 'xmark']" class="wc-close" />
      </button>
    </div>

    <div class="row pl-3 pr-3">
      <span class="wc-clear-btn text-primary pt-1 w-100 text-right" @click="clearAllFilters()">
        {{ $t('clearFilters') }}
      </span>
    </div>

    <div class="row wc-selected-header d-flex">
      <!-- START: Specific Category Text Icon Chips -->
      <span v-for="group in crumbs" :key="group.label">
        <template v-if="group.label.toLowerCase() !== 'all items'">
          <WCTextIconChip
            :text="group.label"
            :id="group.label"
            @click="onSpecificCategoryChip(group)"
            class="mr-2 font-size-sm mb-2"
          />
        </template>
      </span>
      <!-- END: Specific Category Text Icon Chips -->

      <!-- START: All Category Text Icon Chips -->
      <template v-if="!isAllClear() && filteredSelectedValues && filteredSelectedValues.length">
        <template v-for="category in filteredSelectedValues">
          <span v-for="value in category.values" :key="value.name">
            <WCTextIconChip
              :text="value.name"
              v-if="value.selected"
              :id="value.name"
              @click="onChange(category, value, false)"
              class="mr-2 mb-2"
            />
          </span>
        </template>
      </template>
      <!-- END: All Category Text Icon Chips -->
    </div>

    <WCCategoriesFilter
      v-if="groupChildren.length > 0"
      :groupChildren="groupChildren"
      @groupChanged="groupChanged"
    />
    <!-- START - Filter page - The main page when not clicking see more -->
    <div v-if="!showMobileSearchFilterCategory">
      <div v-for="(category, ci) in filteredFacets" :key="ci" class="">
        <div class="pt-1">
          <div class="row wc-category-hdr pl-3 py-1 mb-3">
            {{ category.name }}
          </div>
          <div v-for="value in categoryValues(category)" :key="value.name">
            <WCCheckbox
              :id="'search-filter-checkbox-' + category.key + '-' + value.key"
              class=" p-0 wc-filter-checkbox"
              @update:modelValue="onChange(category, value, $event)"
              :modelValue="value.selected"
              :label="value.name"
            />
            <span class="float-right">{{ value.count }}</span>
            <hr class="row" />
          </div>
          <button
            v-if="category.values.length > 6"
            class="btn btn-link underline-link-from-center wc-more-btn"
            @click="seeMore(category)"
          >
            <font-awesome-icon :icon="['fa', 'plus']" size="xs" class="mr-2" />{{ $t('seeMore') }}
          </button>
        </div>
      </div>
    </div>
    <!-- END : Filter Page -->

    <!-- START : See More Popup -->
    <div v-else class="wc-mobile-filter-pop">
      <MobileSearchFilterCategory
        :facets="facets"
        :categoryFilter="category"
        @onGoBackClick="onClickingGoBack"
      />
    </div>
    <!-- END : See More Popup -->
  </div>
</template>

<script>
import WCCheckbox from '@/modules/forms/components/WCCheckbox/WCCheckbox.vue';
import WCTextIconChip from '@/components/WCTextIconChip/WCTextIconChip.vue';
import FacetFilterMixin from '@/modules/filters/mixins/FacetFilterMixin';
import WCCategoriesFilter from '@/components/WCCategoriesFilter/WCCategoriesFilter.vue';
import MobileSearchFilterCategory from '@/modules/mobileSearch/views/MobileSearchFilterCategory.vue';

export default {
  components: { WCCheckbox, WCTextIconChip, WCCategoriesFilter, MobileSearchFilterCategory },
  props: {
    groupChildren: {
      type: Array,
    },
    crumbs: {
      type: Array,
    },
    facets: {
      type: Array,
    },
  },
  data() {
    return {
      showMobileSearchFilterCategory: false,
      category: {},
    };
  },
  emits: [
    'groupChanged',
    'update:facets',
    'clearSpecificCategories',
    'close-filters',
    'specificCategoryMobileChip',
  ],
  mixins: [FacetFilterMixin], // for filteredFacets
  methods: {
    /**
     * Method to emit the chip clicked event for specific category
     */
    onSpecificCategoryChip(group) {
      this.$emit('specificCategoryMobileChip', group);
    },
    close() {
      this.$emit('close-filters');
    },
    /**
     * To display all the selected values in filter page
     * if none is selected or selected values < 6, then do slice
     */
    categoryValues(category) {
      const selectedValues = category.values.filter(obj => obj.selected === true);
      if (selectedValues.length > 6) return selectedValues;
      return category.values.slice(0, 6);
    },
    /**
     * Opens the see more popup
     * Will assign the current category value
     */
    seeMore(category) {
      this.showMobileSearchFilterCategory = true;
      this.category = category;
    },
    /**
     * on clicking go back, close the popup
     * and emit the filtered facets to browse filter page
     */
    onClickingGoBack() {
      this.showMobileSearchFilterCategory = false;
      this.$emit('update:facets', this.filteredFacets);
    },
    isCategoryClear(category) {
      return !this.facets
        .filter(c => c.key === category.key)
        .some(c => c.values.some(value => value.selected));
    },
    clearAllFilters() {
      // switch back the crumb state to "All Items"
      this.$emit('clearSpecificCategories');
      const copy = this.facets.map(category => {
        const values = category.values.map(facet => ({ ...facet, selected: false }));
        return {
          ...category,
          values,
        };
      });

      this.$emit('update:facets', copy);
    },
    groupChanged(group) {
      this.$emit('groupChanged', group);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/_tools.scss';
@import '~@/assets/styles/_settings.scss';

.wc-filter-checkbox {
  display: inline;
  margin-left: $margin-4;
}

.wc-filters-header {
  background-color: var(--light-darker-1);
  font-size: $font-size-xl;
}

.wc-selected-header {
  margin-left: 0;
  line-height: 2.5;
}

.wc-category-hdr {
  background-color: var(--secondary-lighter-6, get-color('secondary', 'lighter-6'));
  font-size: $font-size-lg;
  font-weight: 700;
}

.wc-clear-btn {
  cursor: pointer;
}

.wc-more-btn {
  margin-left: $margin-1 * 3;
}

.wc-close {
  cursor: pointer;
}

.wc-mobile-filter-pop {
  position: fixed;
  overflow-y: auto;
  background: var(--white);
  z-index: 3000; /* map-get($zindex, 'modals');*/
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
</style>
